import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../utils/api';
import './Blog.css';

export default function Blog() {
  const [posts, setPosts]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [category, setCategory] = useState('All');
  const [error, setError]       = useState('');

  useEffect(() => {
    api.getBlogPosts()
      .then(data => {
        setPosts(data.posts || []);
        setLoading(false);
      })
      .catch(err => {
        // Show the real error message so you can debug it
        setError(err.message || 'Failed to load posts.');
        setLoading(false);
        console.error('Blog load error:', err);
      });
  }, []);

  const cats = ['All', ...Array.from(new Set(posts.map(p => p.category).filter(Boolean)))];

  const filtered = posts.filter(p => {
    const s = search.toLowerCase();
    return (!s || p.title.toLowerCase().includes(s) || (p.excerpt||'').toLowerCase().includes(s))
      && (category === 'All' || p.category === category);
  });

  const fmt = d => {
    try { return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }); }
    catch { return d; }
  };

  return (
    <div className="blog-page">
      <section className="blog-hero">
        <div className="blog-hero__glow" />
        <div className="container">
          <div className="section-tag">Our Blog</div>
          <h1 className="section-title">Insights &amp; <span className="highlight">Updates</span></h1>
          <p className="section-desc">Tips, guides, and news from the MS Discoveries team.</p>
          <div className="blog-search">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
            </svg>
            <input type="text" placeholder="Search articles..." value={search} onChange={e => setSearch(e.target.value)} />
            {search && <button className="blog-search__clear" onClick={() => setSearch('')}>✕</button>}
          </div>
          {cats.length > 1 && (
            <div className="blog-cats">
              {cats.map(c => (
                <button key={c} className={`blog-cat-btn ${category === c ? 'active' : ''}`} onClick={() => setCategory(c)}>{c}</button>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="blog-content">
        <div className="container">
          {loading ? (
            <div className="blog-loading"><div className="blog-spinner"/><p>Loading posts...</p></div>
          ) : error ? (
            <div className="blog-empty">
              <div className="blog-empty__icon">⚠️</div>
              <h3>Something went wrong</h3>
              <p>{error}</p>
              {/* Show helpful hint based on error type */}
              {error.includes('404') && (
                <p style={{color:'#aaa',fontSize:'0.85rem',marginTop:'8px'}}>
                  API not found — check your server proxy or backend is running.
                </p>
              )}
              {error.includes('500') && (
                <p style={{color:'#aaa',fontSize:'0.85rem',marginTop:'8px'}}>
                  Server error — check backend logs (likely a database connection issue).
                </p>
              )}
              {(error.includes('Failed to fetch') || error.includes('NetworkError')) && (
                <p style={{color:'#aaa',fontSize:'0.85rem',marginTop:'8px'}}>
                  Network error — the backend server may be offline or the proxy is not configured.
                </p>
              )}
              <button
                className="btn-outline"
                style={{marginTop:'16px'}}
                onClick={() => { setError(''); setLoading(true); api.getBlogPosts().then(data => { setPosts(data.posts || []); setLoading(false); }).catch(err => { setError(err.message || 'Failed to load posts.'); setLoading(false); }); }}
              >
                Try Again
              </button>
            </div>
          ) : filtered.length === 0 ? (
            <div className="blog-empty">
              <div className="blog-empty__icon">📝</div>
              <h3>{search ? 'No results found' : 'No posts yet'}</h3>
              <p>{search ? `No articles match "${search}"` : 'Check back soon for articles and updates.'}</p>
              {search && <button className="btn-outline" style={{marginTop:'16px'}} onClick={() => setSearch('')}>Clear Search</button>}
            </div>
          ) : (
            <>
              <Link to={`/blog/${filtered[0].slug}`} className="blog-featured">
                <div className="blog-featured__meta">
                  {filtered[0].category && <span className="blog-cat-tag">{filtered[0].category}</span>}
                  <span className="blog-date">{fmt(filtered[0].createdAt)}</span>
                  {filtered[0].readTime && <span className="blog-date">· {filtered[0].readTime} min read</span>}
                </div>
                <h2>{filtered[0].title}</h2>
                <p>{filtered[0].excerpt}</p>
                <div className="blog-featured__footer">
                  <span className="blog-author">By {filtered[0].author || 'MS Discoveries'}</span>
                  <span className="blog-read-more">Read article →</span>
                </div>
              </Link>
              {filtered.length > 1 && (
                <div className="blog-grid">
                  {filtered.slice(1).map(post => (
                    <Link to={`/blog/${post.slug}`} key={post.id} className="blog-card">
                      {post.category && <span className="blog-cat-tag">{post.category}</span>}
                      <h3>{post.title}</h3>
                      <p>{post.excerpt}</p>
                      <div className="blog-card__footer">
                        <span className="blog-date">{fmt(post.createdAt)}</span>
                        <span className="blog-arrow">→</span>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}